import { api } from './index';

import type { momentsBearerQuery as momentsBearerQueryType } from './momentsApi';
import type { BaseQueryApi } from '@reduxjs/toolkit/query';

// Wire types mirror docs/plans/2026-10-23-mcp-member-contracts.md §2 and §4
// exactly (snake_case JSON, Go time.Time as ISO strings). Do not rename.
export type CoachingNoteCategory =
  | 'realized'
  | 'strengths'
  | 'opportunities'
  | 'guidance'
  | 'actions'
  | 'person';
export type CoachingNoteListCategory = Exclude<CoachingNoteCategory, 'person'>;
export type CoachingNoteOrigin = 'generated' | 'member' | 'ai';

export interface SessionRef {
  artifact_id: string;
  date: string;
  label: string;
  session_id: string;
}

export interface NoteItem {
  category: CoachingNoteCategory;
  id: string;
  origin: CoachingNoteOrigin;
  person_id?: string;
  position: number;
  sources: SessionRef[];
  text: string;
  updated_at: string;
}

export interface CoachingPerson {
  id: string;
  items: NoteItem[];
  name: string;
  role: string;
}

export type RunStatusValue = 'queued' | 'running' | 'succeeded' | 'failed';

export interface RunStatus {
  error?: string;
  finished_at?: string;
  id: string;
  status: RunStatusValue;
}

export interface NotesView {
  categories: Partial<Record<CoachingNoteListCategory, NoteItem[]>>;
  coach_first_name: string;
  member_first_name: string;
  people: CoachingPerson[];
  run?: RunStatus;
  updated_at: Partial<Record<CoachingNoteCategory, string>>;
}

export interface AISettings {
  reflections_enabled: boolean;
  transcripts_enabled: boolean;
}

export interface Trigger {
  enabled: boolean;
  id: string;
  origin: CoachingNoteOrigin;
  position: number;
  then: string;
  when: string;
  when_third_person: string;
}

export interface Exclusion {
  id: string;
  text: string;
  text_third_person: string;
}

export interface ToolDescription {
  text: string;
}

export interface CoachingSettingsResponse {
  description: ToolDescription;
  exclusions: Exclusion[];
  settings: AISettings;
  triggers: Trigger[];
}

export interface HeadlinedItem {
  headline: string;
  text: string;
}

export interface AIPrompt {
  text: string;
  when: string;
}

export interface ReflectionV2 {
  actions: string[];
  ai_prompts: AIPrompt[];
  guidance: string[];
  key_insight: string;
  no_prompts_reason: string | null;
  opportunities: HeadlinedItem[];
  questions: string[];
  realized: HeadlinedItem[];
  strengths: HeadlinedItem[];
}

export interface ReflectionView {
  reflection: ReflectionV2;
  revision: number; // artifact revision, used for v2 highlights (contracts §8.11)
  session: SessionRef;
  title: string;
}

export interface TopicIdea {
  headline: string;
  source: SessionRef;
  text: string;
}

export interface CoachingHomeSession {
  key_insight: string;
  session: SessionRef;
  title: string;
}

export interface CoachingHomeNextSession {
  coach_first_name: string;
  starts_at: string;
}

export interface CoachingHomeResponse {
  next_session: CoachingHomeNextSession | null;
  sessions: CoachingHomeSession[];
  topic_ideas: TopicIdea[];
}

export interface ReplaceCategoryLine {
  id?: string;
  text: string;
}

export interface ReplaceCategoryRequest {
  category: CoachingNoteCategory;
  lines: ReplaceCategoryLine[];
  person_id?: string;
}

export interface RestoreCategoryRequest {
  category: CoachingNoteCategory;
  items: NoteItem[];
  person_id?: string;
}

export interface UpsertTriggerRequest {
  enabled: boolean;
  id?: string;
  position?: number; // create only (contracts §8.5)
  then: string;
  when: string;
}

export interface IdRequest {
  id: string;
}

export interface AddExclusionRequest {
  text: string;
}

export interface McpShares {
  notes: true;
  reflections: boolean;
  transcripts: boolean;
}

export interface McpAuthorizationRequest {
  client_name: string;
  expires_at: string;
  redirect_host: string;
  request_id: string;
  scopes: string[];
  shares: McpShares;
}

export type McpDecision = 'allow' | 'deny';

export interface McpDecideRequest {
  decision: McpDecision;
  request_id: string;
}

export interface McpDecideResponse {
  redirect_url: string;
}

export interface McpGrant {
  client_name: string;
  created_at: string;
  id: string;
  last_used_at: string | null;
}

export interface McpGrantsResponse {
  connector_url: string;
  grants: McpGrant[];
}

export interface McpRevokeRequest {
  grant_id: string;
}

export interface McpRevokeResponse {
  ok: true;
}

// Contracts §8.3: lists are never null. Normalizing stays as a cheap guard.
const list = <T>(value: T[] | null | undefined): T[] =>
  Array.isArray(value) ? value : [];

const normalizeItem = (item: NoteItem): NoteItem => ({
  ...item,
  sources: list(item.sources),
});

type NotesViewWire = Omit<NotesView, 'categories' | 'updated_at'> & {
  categories?: Record<string, NoteItem[] | null> | null;
  updated_at?: NotesView['updated_at'] | null;
};

export const normalizeNotesView = (payload: unknown): NotesView => {
  const view = payload as NotesViewWire;
  const categories: NotesView['categories'] = {};
  Object.entries(view.categories ?? {}).forEach(([category, items]) => {
    categories[category as CoachingNoteListCategory] =
      list(items).map(normalizeItem);
  });
  return {
    ...view,
    categories,
    people: list(view.people).map((person) => ({
      ...person,
      items: list(person.items).map(normalizeItem),
    })),
    updated_at: view.updated_at ?? {},
  };
};

export const normalizeSettings = (
  payload: unknown,
): CoachingSettingsResponse => {
  const value = payload as CoachingSettingsResponse;
  return {
    ...value,
    exclusions: list(value.exclusions),
    triggers: list(value.triggers),
  };
};

export const normalizeHome = (payload: unknown): CoachingHomeResponse => {
  const value = payload as Partial<CoachingHomeResponse>;
  return {
    next_session: value.next_session ?? null,
    sessions: list(value.sessions),
    topic_ideas: list(value.topic_ideas),
  };
};

export const normalizeReflection = (payload: unknown): ReflectionView => {
  const value = payload as ReflectionView;
  const r = value.reflection;
  return {
    ...value,
    reflection: {
      actions: list(r.actions),
      ai_prompts: list(r.ai_prompts),
      guidance: list(r.guidance),
      key_insight: r.key_insight,
      no_prompts_reason: r.no_prompts_reason ?? null,
      opportunities: list(r.opportunities),
      questions: list(r.questions),
      realized: list(r.realized),
      strengths: list(r.strengths),
    },
  };
};

export const normalizeGrants = (payload: unknown): McpGrantsResponse => {
  const value = payload as McpGrantsResponse;
  return { connector_url: value.connector_url, grants: list(value.grants) };
};

const identity = <T>(payload: unknown): T => payload as T;

// Lazy import keeps this module import-safe in tests that mock momentsApi
// (same pattern as momentsAppointmentApi.ts).
const momentsBearerRequest = async <T>(
  ...args: Parameters<typeof momentsBearerQueryType<T>>
): Promise<Awaited<ReturnType<typeof momentsBearerQueryType<T>>>> => {
  const { momentsBearerQuery } = await import('./momentsApi');
  return momentsBearerQuery<T>(...args);
};

interface RpcArgs<T> {
  body?: unknown;
  map: (payload: unknown) => T;
  method?: 'GET' | 'POST';
  path: string;
}

// returnErrorOnFailure makes momentsBearerQuery return every failure as an
// error, so fallbackData is never served to a page.
const rpc = <T>(
  queryApi: BaseQueryApi,
  { body, map, method = 'GET', path }: RpcArgs<T>,
) =>
  momentsBearerRequest<T>(queryApi, {
    body,
    fallbackData: undefined as unknown as T,
    mapResponse: map,
    method,
    path,
    returnErrorOnFailure: true,
  });

const withQuery = (path: string, params: Record<string, string>) =>
  `${path}?${new URLSearchParams(params).toString()}`;

// Every settings mutation answers with the full settings payload (contracts
// §4). Writing it into the cache keeps controls that build full objects from
// this entry on the confirmed values while the invalidation refetch runs.
const writeSettingsCache = async (
  _arg: unknown,
  {
    dispatch,
    queryFulfilled,
  }: {
    dispatch: (action: unknown) => unknown;
    queryFulfilled: Promise<{ data: CoachingSettingsResponse }>;
  },
): Promise<void> => {
  try {
    const { data } = await queryFulfilled;
    dispatch(
      // eslint-disable-next-line @typescript-eslint/no-use-before-define -- called after module init
      mcpMemberApi.util.updateQueryData(
        'getCoachingNotesSettings',
        undefined,
        () => data,
      ),
    );
  } catch {
    // The mutation's own error result reports the failure.
  }
};

export const mcpMemberApi = api.injectEndpoints({
  endpoints: (builder) => ({
    addCoachingNotesExclusion: builder.mutation<
      CoachingSettingsResponse,
      AddExclusionRequest
    >({
      invalidatesTags: (_r, error) => (error ? [] : ['CoachingNotesSettings']),
      onQueryStarted: writeSettingsCache,
      queryFn: (body, queryApi) =>
        rpc(queryApi, {
          body,
          map: normalizeSettings,
          method: 'POST',
          path: '/rpc/v1/coaching_notes.addExclusion',
        }),
    }),
    decideMcpAuthorizationRequest: builder.mutation<
      McpDecideResponse,
      McpDecideRequest
    >({
      queryFn: (body, queryApi) =>
        rpc(queryApi, {
          body,
          map: identity<McpDecideResponse>,
          method: 'POST',
          path: '/rpc/v1/mcp_connect.decide',
        }),
    }),
    deleteCoachingNotesExclusion: builder.mutation<
      CoachingSettingsResponse,
      IdRequest
    >({
      invalidatesTags: (_r, error) => (error ? [] : ['CoachingNotesSettings']),
      onQueryStarted: writeSettingsCache,
      queryFn: (body, queryApi) =>
        rpc(queryApi, {
          body,
          map: normalizeSettings,
          method: 'POST',
          path: '/rpc/v1/coaching_notes.deleteExclusion',
        }),
    }),
    deleteCoachingNotesTrigger: builder.mutation<
      CoachingSettingsResponse,
      IdRequest
    >({
      invalidatesTags: (_r, error) => (error ? [] : ['CoachingNotesSettings']),
      onQueryStarted: writeSettingsCache,
      queryFn: (body, queryApi) =>
        rpc(queryApi, {
          body,
          map: normalizeSettings,
          method: 'POST',
          path: '/rpc/v1/coaching_notes.deleteTrigger',
        }),
    }),
    getCoachingNotes: builder.query<NotesView, void>({
      providesTags: ['CoachingNotes'],
      queryFn: (_arg, queryApi) =>
        rpc(queryApi, {
          map: normalizeNotesView,
          path: '/rpc/v1/coaching_notes.get',
        }),
    }),
    getCoachingNotesHome: builder.query<CoachingHomeResponse, void>({
      providesTags: ['CoachingNotesHome'],
      queryFn: (_arg, queryApi) =>
        rpc(queryApi, {
          map: normalizeHome,
          path: '/rpc/v1/coaching_notes.home',
        }),
    }),
    getCoachingNotesReflection: builder.query<
      ReflectionView,
      { artifactId: string }
    >({
      queryFn: ({ artifactId }, queryApi) =>
        rpc(queryApi, {
          map: normalizeReflection,
          path: withQuery('/rpc/v1/coaching_notes.reflection', {
            artifact_id: artifactId,
          }),
        }),
    }),
    getCoachingNotesSettings: builder.query<CoachingSettingsResponse, void>({
      providesTags: ['CoachingNotesSettings'],
      queryFn: (_arg, queryApi) =>
        rpc(queryApi, {
          map: normalizeSettings,
          path: '/rpc/v1/coaching_notes.settings',
        }),
    }),
    getMcpAuthorizationRequest: builder.query<
      McpAuthorizationRequest,
      { requestId: string }
    >({
      queryFn: ({ requestId }, queryApi) =>
        rpc(queryApi, {
          map: identity<McpAuthorizationRequest>,
          path: withQuery('/rpc/v1/mcp_connect.authorizationRequest', {
            request_id: requestId,
          }),
        }),
    }),
    getMcpConnectGrants: builder.query<McpGrantsResponse, void>({
      providesTags: ['McpConnectGrants'],
      queryFn: (_arg, queryApi) =>
        rpc(queryApi, {
          map: normalizeGrants,
          path: '/rpc/v1/mcp_connect.grants',
        }),
    }),
    regenerateCoachingNotes: builder.mutation<RunStatus, void>({
      invalidatesTags: (_r, error) => (error ? [] : ['CoachingNotes']),
      queryFn: (_arg, queryApi) =>
        rpc(queryApi, {
          map: identity<RunStatus>,
          method: 'POST',
          path: '/rpc/v1/coaching_notes.regenerate',
        }),
    }),
    replaceCoachingNotesCategory: builder.mutation<
      NotesView,
      ReplaceCategoryRequest
    >({
      invalidatesTags: (_r, error) => (error ? [] : ['CoachingNotes']),
      queryFn: (body, queryApi) =>
        rpc(queryApi, {
          body,
          map: normalizeNotesView,
          method: 'POST',
          path: '/rpc/v1/coaching_notes.replaceCategory',
        }),
    }),
    restoreCoachingNotesCategory: builder.mutation<
      NotesView,
      RestoreCategoryRequest
    >({
      invalidatesTags: (_r, error) => (error ? [] : ['CoachingNotes']),
      queryFn: (body, queryApi) =>
        rpc(queryApi, {
          body,
          map: normalizeNotesView,
          method: 'POST',
          path: '/rpc/v1/coaching_notes.restoreCategory',
        }),
    }),
    revokeMcpConnectGrant: builder.mutation<
      McpRevokeResponse,
      McpRevokeRequest
    >({
      invalidatesTags: (_r, error) => (error ? [] : ['McpConnectGrants']),
      queryFn: (body, queryApi) =>
        rpc(queryApi, {
          body,
          map: identity<McpRevokeResponse>,
          method: 'POST',
          path: '/rpc/v1/mcp_connect.revoke',
        }),
    }),
    updateCoachingNotesSettings: builder.mutation<
      CoachingSettingsResponse,
      AISettings
    >({
      invalidatesTags: (_r, error) => (error ? [] : ['CoachingNotesSettings']),
      onQueryStarted: writeSettingsCache,
      queryFn: (body, queryApi) =>
        rpc(queryApi, {
          body,
          map: normalizeSettings,
          method: 'POST',
          path: '/rpc/v1/coaching_notes.updateSettings',
        }),
    }),
    upsertCoachingNotesTrigger: builder.mutation<
      CoachingSettingsResponse,
      UpsertTriggerRequest
    >({
      invalidatesTags: (_r, error) => (error ? [] : ['CoachingNotesSettings']),
      onQueryStarted: writeSettingsCache,
      queryFn: (body, queryApi) =>
        rpc(queryApi, {
          body,
          map: normalizeSettings,
          method: 'POST',
          path: '/rpc/v1/coaching_notes.upsertTrigger',
        }),
    }),
  }),
});

export const {
  useAddCoachingNotesExclusionMutation,
  useDecideMcpAuthorizationRequestMutation,
  useDeleteCoachingNotesExclusionMutation,
  useDeleteCoachingNotesTriggerMutation,
  useGetCoachingNotesHomeQuery,
  useGetCoachingNotesQuery,
  useGetCoachingNotesReflectionQuery,
  useGetCoachingNotesSettingsQuery,
  useGetMcpAuthorizationRequestQuery,
  useGetMcpConnectGrantsQuery,
  useRegenerateCoachingNotesMutation,
  useReplaceCoachingNotesCategoryMutation,
  useRestoreCoachingNotesCategoryMutation,
  useRevokeMcpConnectGrantMutation,
  useUpdateCoachingNotesSettingsMutation,
  useUpsertCoachingNotesTriggerMutation,
} = mcpMemberApi;
